/* eslint-disable camelcase */
import { getPercentage, roundByTickSize } from 'helpers/utils';
import { Product } from 'types/IProducts';
import { ProductById } from 'types/ITrade';

import { useTickerStoreBase } from './ticker.store';
import { TickerState, TickerStore } from './ticker.types';

// ======= Store Selectors - To be used with functions outside react (NON-REACTIVE) =======

// #region Store Selectors
const tickerStateSelector = (): TickerState => useTickerStoreBase.getState().state;

const tickerObjectSelector = () => tickerStateSelector().tickerObj;

const tickerObjectBySymbolSelector = (symbol: string) =>
  tickerStateSelector().tickerObj[symbol];

const lastPriceChangeSelectorBySymbol = (symbol: string) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { close, open } = tickerData || {};
  return getPercentage(close, open);
};

const markPriceSelectorByProductId = (productId: number) => {
  const markPriceValue = tickerStateSelector().markPrice.get(productId);
  return markPriceValue;
};

const allMarkPriceSelector = () => tickerStateSelector().markPrice;

const lastPriceSelectorBySymbol = (symbol: string, tickSize: number) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { close } = tickerData || {};
  return roundByTickSize(tickSize)(close);
};

const openPriceSelectorBySymbol = (symbol: string, tickSize: number) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { open } = tickerData || {};
  return roundByTickSize(tickSize)(open);
};

const volumeSelectorBySymbol = (symbol: string) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { volume } = tickerData || {};
  return volume ?? '';
};

const turnoverSelectorBySymbol = (symbol: string) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { turnover_usd } = tickerData || {};
  return turnover_usd ?? '';
};

const spotPriceSelectorBySpotSymbol = (spotIndexSymbol: string) => {
  const spotPriceValue = tickerStateSelector().spotPrice[spotIndexSymbol];
  return spotPriceValue;
};

const allSpotPriceSelector = () => tickerStateSelector().spotPrice;

const indexPriceSelectorBySpotSymbol = (spotIndexSymbol: string, tickSize: number) => {
  const spotPriceValue = spotPriceSelectorBySpotSymbol(spotIndexSymbol);
  return roundByTickSize(tickSize)(spotPriceValue);
};

const highSelectorBySymbol = (symbol: string, tickSize: number) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { high } = tickerData || {};
  return roundByTickSize(tickSize)(high);
};

const lowSelectorBySymbol = (symbol: string, tickSize: number) => {
  const tickerData = tickerStateSelector().tickerObj[symbol];
  const { low } = tickerData || {};
  return roundByTickSize(tickSize)(low);
};

const oiSelectorByProductId = (productId: number) => {
  const oiValue = tickerStateSelector().oi.get(productId);
  return oiValue;
};

const oiChangeSelectorByProductId = (productId: number) => {
  const oiChangeValue = tickerStateSelector().oiChange.get(productId);
  return oiChangeValue;
};

const fundingRateSelectorBySymbol = (symbol: string) => {
  const fundingRateValue = tickerStateSelector().tickerObj[symbol]?.funding_rate;
  return fundingRateValue ? Number(fundingRateValue).toFixed(4) : 0;
};

const fundingRateSelectorById = (productId: number) => {
  const fundingRateValue =
    tickerStateSelector().tickerMapById.get(productId)?.funding_rate;
  return fundingRateValue ? Number(fundingRateValue).toFixed(4) : 0;
};

const premiumSelectorByIdAndSpotSymbol = (productId: number, spotIndexSymbol: string) => {
  const markPriceValue = tickerStateSelector().markPrice.get(productId);
  const spotPriceValue = tickerStateSelector().spotPrice[spotIndexSymbol];
  return getPercentage(markPriceValue, spotPriceValue);
};

const thetaSelectorByProductId = (productId: number) => {
  const thetaValue = tickerStateSelector().greeks.theta.get(productId);
  return thetaValue;
};

const gammaSelectorByProductId = (productId: number) => {
  const gammaValue = tickerStateSelector().greeks.gamma.get(productId);
  return gammaValue;
};

const deltaSelectorByProductId = (productId: number) => {
  const deltaValue = tickerStateSelector().greeks.delta.get(productId);
  return deltaValue;
};

const vegaSelectorByProductId = (productId: number) => {
  const vegaValue = tickerStateSelector().greeks.vega.get(productId);
  return vegaValue;
};

const bidQtySelectorByProductId = (productId: number) => {
  const bidQtyValue = tickerStateSelector().bidQty.get(productId);
  return bidQtyValue;
};

const askQtySelectorByProductId = (productId: number) => {
  const askQtyValue = tickerStateSelector().askQty.get(productId);
  return askQtyValue;
};

const askIvSelectorByProductId = (productId: number) => {
  const askIVValue = tickerStateSelector().askIV.get(productId);
  return askIVValue;
};

const bidIvSelectorByProductId = (productId: number) => {
  const bidIVValue = tickerStateSelector().bidIV.get(productId);
  return bidIVValue;
};

const bestAskSelectorByProductId = (productId: number) => {
  const bestAskValue = tickerStateSelector().bestAsk.get(productId);
  return bestAskValue;
};

const bestBuySelectorByProductId = (productId: number) => {
  const bestBuyValue = tickerStateSelector().bestBuy.get(productId);
  return bestBuyValue;
};

const impliedVolatilitySelectorByProductId = (productId: number) => {
  const impliedVolatilityValue = tickerStateSelector().impliedVolatility.get(productId);
  return (Number(impliedVolatilityValue) * 100).toFixed(1);
};

const allCallOiSelector = () => {
  const { callOi } = tickerStateSelector();
  return callOi;
};

const allPutOiSelector = () => {
  const { putOi } = tickerStateSelector();
  return putOi;
};

const allCallTurnoverUsdSelector = () => {
  const { callTurnoverUsd } = tickerStateSelector();
  return callTurnoverUsd;
};

const allPutTurnoverUsdSelector = () => {
  const { putTurnoverUsd } = tickerStateSelector();
  return putTurnoverUsd;
};

// #endregion

// ========  Data Selectors - To be used with components (Reactive Data) ========
// Usage -> const getLastPriceValue = useTickerStoreBase(selectCloseBySymbol(symbol));

// #region Reactive Data Selectors

const selectAllTickerObject = (store: TickerStore) => store.state.tickerObj;

const selectTickerDataBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol];

const selectProductIdBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.product_id;

const selectVolumeBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.volume;

const selectCloseBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.close;

const selectHighBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.high;

const selectLowBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.low;

const selectAllOptionsTickerObject = (store: TickerStore) => store.state.optionsTickerObj;

const selectAllOptionsTickerArray = (store: TickerStore) =>
  Object.values(store.state.optionsTickerObj);

const selectMarkPriceById = (id: number) => (store: TickerStore) =>
  store.state.markPrice.get(id);

const selectAllMarkPrice = (store: TickerStore) => store.state.markPrice;

const selectOpenPriceBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.open;

const selectOiById = (id: number) => (store: TickerStore) => store.state.oi.get(id);

const selectOiChangeById = (id: number) => (store: TickerStore) =>
  store.state.oiChange.get(id);

const selectSpotPriceBySpotSymbol = (spotSymbol: string) => (store: TickerStore) =>
  store.state.spotPrice[spotSymbol];

const selectSpotPriceObject = (store: TickerStore) => store.state.spotPrice;

const selectTickerData = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol];

const selectTickerDataById = (id: number) => (store: TickerStore) =>
  store.state.tickerMapById.get(id);

const selectFundingRateBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.funding_rate;

const selectTurnoverBySymbol = (symbol: string) => (store: TickerStore) =>
  store.state.tickerObj[symbol]?.turnover_usd;

const selectAskQtyById = (id: number) => (store: TickerStore) =>
  store.state.askQty.get(id);
const selectAskIVById = (id: number) => (store: TickerStore) => store.state.askIV.get(id);
const selectBidQtyById = (id: number) => (store: TickerStore) =>
  store.state.bidQty.get(id);
const selectBidIVById = (id: number) => (store: TickerStore) => store.state.bidIV.get(id);
const selectMarkIVById = (id: number) => (store: TickerStore) =>
  store.state.markIV.get(id);
const selectAskPriceById = (id: number) => (store: TickerStore) =>
  store.state.bestAsk.get(id);
const selectBidPriceById = (id: number) => (store: TickerStore) =>
  store.state.bestBuy.get(id);

const selectAllImpliedVolatility = (store: TickerStore) => store.state.impliedVolatility;

/**
 * Note: (Number(impliedVolatilityValue) * 100).toFixed(1); needed
 */
const selectImpliedVolatilityById = (id: number) => (store: TickerStore) =>
  store.state.impliedVolatility.get(id);

const selectAllGamma = (store: TickerStore) => store.state.greeks.gamma;
const selectAllDelta = (store: TickerStore) => store.state.greeks.delta;
const selectAllTheta = (store: TickerStore) => store.state.greeks.theta;
const selectAllVega = (store: TickerStore) => store.state.greeks.vega;

const selectThetaById = (id: number) => (store: TickerStore) =>
  store.state.greeks.theta.get(id);
const selectGammaById = (id: number) => (store: TickerStore) =>
  store.state.greeks.gamma.get(id);
const selectDeltaById = (id: number) => (store: TickerStore) =>
  store.state.greeks.delta.get(id);
const selectVegaById = (id: number) => (store: TickerStore) =>
  store.state.greeks.vega.get(id);

type GreekKey = 'delta' | 'gamma' | 'theta' | 'vega';

/**
 * @summary Selector factory that extracts greek values for given product IDs
 * as a plain object. Memoize the return value with useMemo so that
 * useShallow receives a stable selector reference across renders.
 *
 * @example
 * const selector = useMemo(
 *   () => selectGreeksByProductIds(productIds, 'delta'),
 *   [productIds]
 * );
 * const deltaObj = useTickerStoreBase(useShallow(selector));
 */
const selectGreeksByProductIds =
  (productIds: number[], greekKey: GreekKey) => (store: TickerStore) => {
    const greekMap = store.state.greeks[greekKey];
    const result: Record<number, unknown> = {};
    for (let i = 0; i < productIds.length; i += 1) {
      result[productIds[i]] = greekMap.get(productIds[i]);
    }
    return result;
  };

const selectCallOiByProductId = (id: number) => (store: TickerStore) =>
  store.state.callOi.get(id);

const selectPutOiByProductId = (id: number) => (store: TickerStore) =>
  store.state.putOi.get(id);

/**
 * @description Selector for all call OI values (Map<productId, oiValue>)
 * Returns the Map directly from store
 */
const selectAllCallOi = (store: TickerStore) => store.state.callOi;

/**
 * @description Selector for all put OI values (Map<productId, oiValue>)
 * Returns the Map directly from store
 */
const selectAllPutOi = (store: TickerStore) => store.state.putOi;

/**
 * @description Selector for all OI values (Map<productId, oiValue>)
 * Returns the Map directly from store
 */
const selectAllOi = (store: TickerStore) => store.state.oi;

/**
 * @description Selector for all OI change values (Map<productId, oiChangeValue>)
 * Returns the Map directly from store
 */
const selectAllOiChange = (store: TickerStore) => store.state.oiChange;

const selectAllCallTurnoverUsd = (store: TickerStore) => store.state.callTurnoverUsd;

const selectAllPutTurnoverUsd = (store: TickerStore) => store.state.putTurnoverUsd;

const selectTotalOptionsOpenInterest = (store: TickerStore) => {
  return Object.values(store.state.optionsTickerObj).reduce((accumulated, current) => {
    const oi = store.state.oi.get(current.product_id!);
    return oi ? accumulated + Number(oi) : accumulated;
  }, 0);
};

/**
 * @description Creates a selector that filters tickers by contract type predicate.
 * This is a Zustand-based reactive selector that can be used with useTickerStoreBase.
 * It requires products from Redux state to be passed as a parameter.
 *
 * @param products - ProductById object from Redux state
 * @param predicate - Function that takes contract_type and returns boolean
 * @returns Zustand selector function
 *
 * @example
 * ```ts
 * // In a React component:
 * const products = useAppSelector(productsSelector);
 * const optionsTickers = useTickerStoreBase(
 *   useShallow(selectFilteredTickersByContractType(products, isCallPutOptions))
 * );
 * ```
 */
const selectFilteredTickersByContractType = (
  products: ProductById,
  predicate: (contractType: string) => boolean
) => {
  return (store: TickerStore) => {
    const allTickers = Object.values(store.state.tickerObj);

    return allTickers.filter(({ product_id }) => {
      const currentProduct: Product | undefined = product_id
        ? products[product_id]
        : undefined;
      if (!currentProduct) {
        return false;
      }
      return (
        currentProduct &&
        predicate(String(currentProduct.contract_type)) &&
        currentProduct.state === 'live'
      );
    });
  };
};
// #endregion

export {
  // #region Store Selectors NON-REACTIVE
  tickerStateSelector,
  tickerObjectSelector,
  tickerObjectBySymbolSelector,
  lastPriceChangeSelectorBySymbol,
  selectVolumeBySymbol,
  openPriceSelectorBySymbol,
  markPriceSelectorByProductId,
  allMarkPriceSelector,
  lastPriceSelectorBySymbol,
  volumeSelectorBySymbol,
  turnoverSelectorBySymbol,
  premiumSelectorByIdAndSpotSymbol,
  spotPriceSelectorBySpotSymbol,
  indexPriceSelectorBySpotSymbol,
  highSelectorBySymbol,
  lowSelectorBySymbol,
  oiSelectorByProductId,
  oiChangeSelectorByProductId,
  fundingRateSelectorBySymbol,
  fundingRateSelectorById,
  thetaSelectorByProductId,
  gammaSelectorByProductId,
  deltaSelectorByProductId,
  vegaSelectorByProductId,
  bidQtySelectorByProductId,
  askQtySelectorByProductId,
  askIvSelectorByProductId,
  bidIvSelectorByProductId,
  bestAskSelectorByProductId,
  bestBuySelectorByProductId,
  impliedVolatilitySelectorByProductId,
  allCallOiSelector,
  allPutOiSelector,
  allCallTurnoverUsdSelector,
  allPutTurnoverUsdSelector,
  allSpotPriceSelector,
  // #endregion

  // #region Data Selectors REACTIVE (use with useTickerStoreBase)
  selectAllTickerObject,
  selectProductIdBySymbol,
  selectFundingRateBySymbol,
  selectTurnoverBySymbol,
  selectCloseBySymbol,
  selectHighBySymbol,
  selectLowBySymbol,
  selectAllMarkPrice,
  selectMarkPriceById,
  selectOpenPriceBySymbol,
  selectOiById,
  selectOiChangeById,
  selectSpotPriceBySpotSymbol,
  selectTickerData,
  selectTickerDataBySymbol,
  selectTickerDataById,
  selectThetaById,
  selectGammaById,
  selectDeltaById,
  selectVegaById,
  selectAllGamma,
  selectAllDelta,
  selectAllTheta,
  selectAllVega,
  selectGreeksByProductIds,
  selectAskQtyById,
  selectBidQtyById,
  selectAskIVById,
  selectBidIVById,
  selectMarkIVById,
  selectAskPriceById,
  selectBidPriceById,
  selectImpliedVolatilityById,
  selectSpotPriceObject,
  selectCallOiByProductId,
  selectPutOiByProductId,
  selectAllOptionsTickerObject,
  selectAllOptionsTickerArray,
  selectTotalOptionsOpenInterest,
  selectAllCallOi,
  selectAllPutOi,
  selectAllOi,
  selectAllOiChange,
  selectAllImpliedVolatility,
  selectFilteredTickersByContractType,
  selectAllCallTurnoverUsd,
  selectAllPutTurnoverUsd,
  // #endregion
};
